import ImageSelector from "components/Account/ImageSelector";
import Status from "components/General/Status";
import ModalComponent from "components/Modal/ModalComponent";
import TagInput from "components/Tags/TagInput";
import { fetchHandleGetAccounts } from "helpers/Account/AccountHelpers";
import { TokenContext } from "helpers/constants/Contexts";
import {
  defaultOrganisationErrorState,
  defaultUpdateOrganisationRequest,
} from "helpers/constants/OrganisationConstants";
import { getOrgUploadToken } from "helpers/Files/FileHelpers";
import {
  fetchHandleDeleteOrganisation,
  fetchHandleUpdateOrganisation,
} from "helpers/Organisations/OrganisationHelpers";
import { IGetAccountsRequest } from "interfaces/Account/IGetAccountsRequest";
import { IButtonProps } from "interfaces/General/IButtonProps";
import { IFile } from "interfaces/General/IFile";
import { IUpdateOrganisationRequestObject } from "interfaces/Organisation/IUpdateOrganisationRequest";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Form, Stack } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { organisationsStateSelector } from "redux/states/stateSelectors";
import { IOrganisation } from "../../interfaces/Organisation/IOrganisation";
import FormFooter from "../Footers/FormFooter";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import AlertMessage from "../Message/AlertMessage";
import styles from "./EditOrganisation.module.scss";

const EditOrganisationComponent = () => {
  // Constants
  const accessToken = useContext(TokenContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handleSubmit } = useForm();
  const { orgId } = useParams();
  const organisationState = useSelector(organisationsStateSelector);
  const cancelUrl = useMemo(
    () => (orgId ? `/organisations/${orgId}` : "/organisations"),
    [orgId]
  );

  // State
  const [organisation, setOrganisation] =
    useState<IUpdateOrganisationRequestObject>(
      defaultUpdateOrganisationRequest
    );
  const [organisationChanges, setOrganisationChanges] =
    useState<IUpdateOrganisationRequestObject>({});
  const [baseOrganisation, setBaseOrganisation] =
    useState<IUpdateOrganisationRequestObject>();
  const [imageToUpload, setImageToUpload] = useState<IFile | null>(null);
  const [initials, setInitials] = useState<string>("");
  const [validated, setValidated] = useState(false);
  const [errorState, setErrorState] = useState(defaultOrganisationErrorState);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [failure, setFailure] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] =
    useState("Saving changes...");

  // Form validation
  const validate = useCallback(() => {
    setErrorState({
      organisationName:
        organisationChanges?.organisationName?.trim().length === 0,
      organisationEmail:
        organisationChanges?.organisationEmail !== undefined
          ? !/\S+@\S+\.\S+/.test(organisationChanges.organisationEmail)
          : false,
    });
  }, [organisationChanges]);

  // Check for changes
  useEffect(() => {
    validate();
    setChangesMade(
      JSON.stringify(organisation) !== JSON.stringify(baseOrganisation)
    );
  }, [organisation, baseOrganisation, validate]);

  // Initialize data from state
  useEffect(() => {
    if (!orgId) {
      navigate("/organisations");
      return;
    }

    const org = organisationState.organisations.find(
      (o) => o.organisationId === orgId
    );

    if (!org) {
      navigate("/organisations");
      return;
    }

    const formOrg: IUpdateOrganisationRequestObject = {
      organisationName: org.organisationName,
      organisationEmail: org.email,
      organisationDescription: org.description,
      organisationPictureUrl: org.organisationPictureUrl,
      organisationTags: org.tags,
      suspended: org.suspended,
      archived: org.archived,
    };

    setOrganisation(formOrg);
    setBaseOrganisation(formOrg);
    setInitials(org.organisationName ? org.organisationName.charAt(0) : "");
  }, [orgId, organisationState.organisations, navigate]);

  // Show validation message effect
  useEffect(() => {
    if (validated) {
      setMessage("Please fill out the required fields.");
      setFailure(true);
      setShowMessage(true);
      window.scrollTo(0, 0);
    }
  }, [validated]);

  // Event Handlers
  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setOrganisation((prev) => ({
      ...prev,
      [name]: value,
    }));
    setOrganisationChanges((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleCheckChange = useCallback((event) => {
    const { name, checked } = event.target;
    setOrganisation((prev) => ({
      ...prev,
      [name]: checked,
    }));
    setOrganisationChanges((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const onTagsUpdated = useCallback((updatedTags: string[]) => {
    setOrganisation((prev) => ({
      ...prev,
      organisationTags: updatedTags,
    }));
    setOrganisationChanges((prev) => ({
      ...prev,
      organisationTags: updatedTags,
    }));
  }, []);

  const handleImageChange = useCallback((event) => {
    if (event.target.files && event.target.files[0]) {
      const file: IFile = {
        path: event.target.files[0],
        name: event.target.files[0].name,
      };
      setImageToUpload(file);
      setOrganisation((prev) => ({
        ...prev,
        organisationPictureUrl: URL.createObjectURL(event.target.files[0]),
      }));
      setOrganisationChanges((prev) => ({
        ...prev,
        organisationPictureUrl: "pendingUpload",
      }));
    }
  }, []);

  const handleImageDelete = useCallback(() => {
    setOrganisation((prev) => ({
      ...prev,
      organisationPictureUrl: "",
    }));
    setOrganisationChanges((prev) => ({
      ...prev,
      organisationPictureUrl: "",
    }));
    setImageToUpload(null);
  }, []);

  // Submit handlers
  const onUpdateOrganisationSuccess = useCallback(
    (org: IOrganisation) => {
      if (orgId) {
        const accountsRequest: IGetAccountsRequest = {
          OrganisationId: orgId,
          LicenceTypeId: null,
          SortBy: null,
          SearchValue: null,
        };
        dispatch(fetchHandleGetAccounts(accessToken, accountsRequest));
      }
      navigate(cancelUrl);
    },
    [accessToken, cancelUrl, dispatch, navigate, orgId]
  );

  const onUpdateOrganisationError = useCallback((message: string) => {
    setMessage(message);
    setFailure(true);
    setShowMessage(true);
    window.scrollTo(0, 0);
  }, []);

  const updateOrganisation = useCallback(
    async (organisation: IUpdateOrganisationRequestObject) => {
      if (!orgId) return;

      setValidated(false);
      setIsSubmitting(true);
      setSubmissionMessage("Updating organisation...");

      try {
        if (imageToUpload) {
          try {
            setSubmissionMessage("Uploading image...");
            const uploadUrl = await getOrgUploadToken(
              accessToken,
              orgId,
              imageToUpload.name
            );

            organisation.organisationPictureUrl = uploadUrl.split("?")[0];
            setSubmissionMessage("Saving changes...");
          } catch (uploadError) {
            console.error("Image upload error:", uploadError);
            setIsSubmitting(false);
            onUpdateOrganisationError(
              "Failed to upload the organisation image. Please try again."
            );
            return; // Exit early to prevent organization update with failed image
          }
        }

        dispatch(
          fetchHandleUpdateOrganisation(
            accessToken,
            orgId,
            organisation,
            (org: IOrganisation) => {
              setIsSubmitting(false);
              onUpdateOrganisationSuccess(org);
            },
            (message: string) => {
              setIsSubmitting(false);
              onUpdateOrganisationError(message);
            }
          )
        );
      } catch (e) {
        console.error("Update organisation error:", e);
        setIsSubmitting(false);
        onUpdateOrganisationError(
          "Failed to send the organisation update request."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      accessToken,
      dispatch,
      imageToUpload,
      onUpdateOrganisationError,
      onUpdateOrganisationSuccess,
      orgId,
    ]
  );

  const deleteOrganisation = useCallback(async () => {
    if (!orgId) return;

    setValidated(false);
    setIsSubmitting(true);
    setSubmissionMessage("Archiving organisation...");

    try {
      dispatch(
        fetchHandleDeleteOrganisation(
          accessToken,
          orgId,
          (org: IOrganisation) => {
            setIsSubmitting(false);
            onUpdateOrganisationSuccess(org);
          },
          (message: string) => {
            setIsSubmitting(false);
            onUpdateOrganisationError(message);
          }
        )
      );
    } catch (error) {
      console.error("Delete organisation error:", error);
      setIsSubmitting(false);
      onUpdateOrganisationError("Failed to delete the organisation.");
    }
  }, [
    accessToken,
    dispatch,
    onUpdateOrganisationError,
    onUpdateOrganisationSuccess,
    orgId,
  ]);

  const onSubmit = useCallback(() => {
    window.scrollTo(0, 0);

    if (!organisationChanges || Object.keys(organisationChanges).length === 0) {
      return;
    }

    if (organisationChanges.archived) {
      deleteOrganisation();
    } else if (!errorState.organisationName && !errorState.organisationEmail) {
      updateOrganisation(organisationChanges);
    } else {
      setValidated(true);
    }
  }, [
    organisationChanges,
    errorState.organisationName,
    errorState.organisationEmail,
    deleteOrganisation,
    updateOrganisation,
  ]);

  const onCancel = useCallback(() => {
    if (changesMade) {
      setShowAlert(true);
    } else {
      navigate(cancelUrl);
    }
  }, [changesMade, cancelUrl, navigate]);

  const handleClose = useCallback(() => {
    setShowAlert(false);
  }, []);

  // Button props
  const primaryBtn: IButtonProps = useMemo(
    () => ({
      text: "Save Changes",
      onClick: () => handleSubmit(onSubmit)(),
      disabled: !changesMade,
    }),
    [changesMade, handleSubmit, onSubmit]
  );

  const secondaryBtn: IButtonProps = useMemo(
    () => ({
      text: "Cancel",
      onClick: onCancel,
    }),
    [onCancel]
  );

  return (
    <>
      <div className="form-page">
        <div className="vstack gap-[24px] w-full max-w-[600px] mx-auto">
          <h3 className="text-heading-1">Edit Organisation</h3>
          {showMessage && (
            <AlertMessage
              success={!failure}
              title={failure ? "Error" : "Success"}
              message={message}
            />
          )}
          <Form
            noValidate={true}
            onSubmit={handleSubmit(onSubmit)}
            className={styles.form}
          >
            <Stack direction="vertical" gap={4}>
              <Stack direction="horizontal" className="gap-10">
                <Form.Label data-variant="label-2-bold">Status:</Form.Label>
                <Status
                  suspended={organisation.suspended}
                  archived={organisation.archived}
                />
              </Stack>
              <Form.Group>
                <Stack direction="horizontal" className="gap-24">
                  <div className="flex gap-10 items-center">
                    <Form.Label data-variant="label-2-bold">Suspend</Form.Label>
                    <Form.Check
                      disabled={baseOrganisation?.archived}
                      type="switch"
                      name="suspended"
                      id="suspend-switch"
                      checked={organisation?.suspended}
                      onChange={handleCheckChange}
                    />
                  </div>
                  <div className="flex gap-10 items-center">
                    <Form.Label data-variant="label-2-bold">Archive</Form.Label>
                    <Form.Check
                      type="switch"
                      name="archived"
                      id="archive-switch"
                      checked={organisation?.archived}
                      onChange={handleCheckChange}
                    />
                  </div>
                </Stack>
              </Form.Group>
              <div className="flex justify-between">
                <Form.Group className={styles.formGroup}>
                  <Form.Label>Organisation Image</Form.Label>
                  <Form.Text className="text-muted">Upload image.</Form.Text>
                </Form.Group>
                <ImageSelector
                  imageUrl={organisation?.organisationPictureUrl}
                  onChange={handleImageChange}
                  onDeleted={handleImageDelete}
                  altStr={initials}
                />
              </div>

              <Form.Group className={styles.formGroup}>
                <Form.Label>Organisation Name</Form.Label>
                <Form.Text className="text-muted">
                  Enter the name of the organisation
                </Form.Text>
                <Form.Control
                  size="lg"
                  name="organisationName"
                  type="text"
                  placeholder="My Organisation Ltd"
                  value={organisation.organisationName || ""}
                  onChange={handleChange}
                  disabled={baseOrganisation?.archived}
                  isInvalid={errorState.organisationName && validated}
                  isValid={!errorState.organisationName && validated}
                />
              </Form.Group>

              <Form.Group className={styles.formGroup}>
                <Form.Label>Organisation Email</Form.Label>
                <Form.Text className="text-muted">
                  Enter the primary email address for this organisation
                </Form.Text>
                <Form.Control
                  size="lg"
                  name="organisationEmail"
                  type="email"
                  placeholder="example@organisation.com"
                  value={organisation.organisationEmail || ""}
                  onChange={handleChange}
                  disabled={baseOrganisation?.archived}
                  isInvalid={errorState.organisationEmail && validated}
                  isValid={!errorState.organisationEmail && validated}
                />
              </Form.Group>

              <Form.Group className={styles.formGroup}>
                <Form.Label>Organisation description</Form.Label>
                <Form.Text className="text-muted">
                  Enter a description for the organisation
                </Form.Text>
                <Form.Control
                  as="textarea"
                  maxLength={1500}
                  rows={3}
                  size="lg"
                  name="organisationDescription"
                  type="text"
                  placeholder="A short description of the organisation max length 1500 characters."
                  value={organisation.organisationDescription || ""}
                  onChange={handleChange}
                  disabled={baseOrganisation?.archived}
                />
              </Form.Group>

              <TagInput
                tagList={organisation.organisationTags || []}
                onTagsUpdated={onTagsUpdated}
                disabled={baseOrganisation?.archived}
                formClass={styles.formGroup}
              />
            </Stack>
          </Form>
        </div>
      </div>
      <FormFooter primaryBtn={primaryBtn} secondaryBtn={secondaryBtn} />
      <ModalComponent
        actionHandler={() => navigate(cancelUrl)}
        handleClose={handleClose}
        displayModal={showAlert}
        action={"Yes"}
        modalTitle={"Cancel Organisation Changes"}
        modalBody={
          "Your unsaved changes will be lost are you sure you want to cancel?"
        }
      />
      <LoadingOverlay isLoading={isSubmitting} message={submissionMessage} />
    </>
  );
};

export default EditOrganisationComponent;
